import { inject, Injectable, Injector } from '@angular/core';
import { SelectWidget } from '@app/finxone-web-frontend/app/lib/actions/grid.action';
import { AlertHandlerService } from '@finxone-platform/shared/services';
import {
  CanvasZone,
  cryptoRandomUUID,
  Role,
  SupportedDevice,
  WorkflowPageConfig,
  WorkflowWidgetConfig,
  WorkflowZoneConfig,
} from '@finxone-platform/shared/sys-config-types';
import { Action, Selector, SelectorOptions, State, StateContext } from '@ngxs/store';
import { HighlightSelectedWidgets } from '../actions/app-grid.action';
import {
  CopyPage,
  CopyPersona,
  CopyRenderingEngineSelectedWidgets,
  CopySelectedWidgets,
  CopyWidget,
  CopyWidgets,
  CopyZone,
  CopyZoneDevice,
  DeleteSelectedWidgets,
  PastePage,
  PastePageProperties,
  PastePersona,
  PasteWidget,
  PasteZone,
  PasteZoneDevice,
} from '../actions/copy-paste.action';
import { WorkflowCommonService } from '../services/workflow-config/workflow.common.service';
import { WorkflowEditorService } from '../services/workflow-editor/workflow-editor.service';
import { PageNavigationMapService } from '../services/page-navigation-map/page-navigation-map.service';
import { ActiveConfigService } from '../services/active-config/active-config.service';

export interface CopyPasteStateModel {
  copiedWidget: WorkflowWidgetConfig[] | null;
  copiedPage: WorkflowPageConfig | null;
  copiedPersona: Role | null;
  copiedZone: { config: CanvasZone; zone: string } | null;
  copiedZoneDevice: {
    [device in SupportedDevice]: WorkflowZoneConfig | null;
  };
}

@SelectorOptions({ injectContainerState: false })
@State<CopyPasteStateModel>({
  name: 'copyPasteState',
  defaults: {
    copiedWidget: null,
    copiedPage: null,
    copiedZone: null,
    copiedZoneDevice: {
      mobile: null,
      tablet: null,
      desktop: null,
    },
    copiedPersona: null,
  },
  children: [],
})
@Injectable()
export class CopyPasteState {
  private workflowCommonService = inject(WorkflowCommonService);
  private alertHandlerService = inject(AlertHandlerService);
  private injector = inject(Injector);
  private _workflowEditorService?: WorkflowEditorService;
  private get workflowEditorService(): WorkflowEditorService {
    return (this._workflowEditorService ??= this.injector.get(WorkflowEditorService));
  }

  selectedWidgets: string[] = [];

  @Selector()
  static getState(state: CopyPasteStateModel) {
    return state;
  }

  @Action(SelectWidget)
  selectWidget(ctx: StateContext<CopyPasteStateModel>, action: SelectWidget) {
    if (action.multiSelect) {
      if (!this.selectedWidgets.includes(action.uniqueId)) this.selectedWidgets.push(action.uniqueId);
    } else {
      this.selectedWidgets = [action.uniqueId];
    }
    ctx.dispatch(new HighlightSelectedWidgets(action.uniqueId, action.multiSelect || false));
  }

  @Action(CopySelectedWidgets)
  copySelectedWidgets(ctx: StateContext<CopyPasteStateModel>) {
    ctx.patchState({
      copiedWidget: this.workflowEditorService.getCurrentPageWidgets().filter((widget) => {
        return this.selectedWidgets.includes(widget.uniqueId.toString());
      }),
    });

    this.alertHandlerService.showAlertFn('success', 'Copied selected widgets');
  }

  @Action(CopyRenderingEngineSelectedWidgets)
  copyRenderingEngineSelectedWidgets() {
    this.workflowEditorService.copyWidgetsFromPage(this.selectedWidgets);
    this.alertHandlerService.showAlertFn('success', 'Copied selected widgets');
  }

  @Action(DeleteSelectedWidgets)
  deleteSelectedWidgets() {
    this.selectedWidgets.forEach((uniqueId) => {
      this.workflowEditorService.removeWidgetFromPage(uniqueId);
    });
  }

  @Action(CopyWidget)
  copyWidget(ctx: StateContext<CopyPasteStateModel>, action: CopyWidget) {
    return ctx.patchState({
      copiedWidget: [action.widgetConfig],
    });
  }

  @Action(CopyWidgets)
  copyWidgets(ctx: StateContext<CopyPasteStateModel>, action: CopyWidgets) {
    return ctx.patchState({
      copiedWidget: action.widgetConfigs,
    });
  }

  @Action(PasteWidget)
  pasteWidget(ctx: StateContext<CopyPasteStateModel>, action: PasteWidget) {
    const widgets = ctx.getState().copiedWidget;

    if (widgets) {
      widgets.forEach((widget) => {
        this.workflowEditorService.addWidgetToZoneAndPage(widget, action?.targetPageLayout);
      });

      this.alertHandlerService.showAlertFn('success', 'Widget Pasted!');
    } else {
      this.alertHandlerService.showAlertFn('error', 'No widget has been copied');
    }
  }

  @Action(PastePersona)
  pastePersona(ctx: StateContext<CopyPasteStateModel>, action: PastePersona) {
    const persona = ctx.getState().copiedPersona;

    if (persona) {
      this.workflowEditorService.addRoleConfig(persona, action.targetPersona);

      this.alertHandlerService.showAlertFn('success', 'Persona Pasted!');
    } else {
      this.alertHandlerService.showAlertFn('error', 'No persona has been copied');
    }
  }

  @Action(PasteZone)
  pasteZone(ctx: StateContext<CopyPasteStateModel>, action: PasteZone) {
    const zone = ctx.getState().copiedZone;

    if (zone) {
      this.workflowEditorService.overwriteZone(zone.config, zone.zone, action.targetPersona);

      this.alertHandlerService.showAlertFn('success', 'Zone Pasted!');
    } else {
      this.alertHandlerService.showAlertFn('error', 'No zone has been copied');
    }
  }

  @Action(CopyZone)
  copyZone(ctx: StateContext<CopyPasteStateModel>, action: CopyZone) {
    this.alertHandlerService.showAlertFn('success', 'Zone Copied!');
    return ctx.patchState({
      copiedZone: {
        config: JSON.parse(JSON.stringify(action.zoneConfig)),
        zone: action.zone,
      },
    });
  }

  @Action(PasteZoneDevice)
  pasteZoneDevice(ctx: StateContext<CopyPasteStateModel>, action: PasteZoneDevice) {
    const zoneDevice = ctx.getState().copiedZoneDevice[action.device];

    if (zoneDevice) {
      this.workflowEditorService.overwriteZoneDevice(
        zoneDevice,
        action.device,
        action.zone,
        action.targetPersona,
      );

      this.alertHandlerService.showAlertFn('success', 'Zone Device Pasted!');
    } else {
      this.alertHandlerService.showAlertFn('error', 'No zone device has been copied');
    }
  }

  @Action(CopyZoneDevice)
  copyZoneDevice(ctx: StateContext<CopyPasteStateModel>, action: CopyZoneDevice) {
    this.alertHandlerService.showAlertFn('success', 'Zone Device Copied!');
    return ctx.patchState({
      copiedZoneDevice: {
        ...ctx.getState().copiedZoneDevice,
        [action.device]: JSON.parse(JSON.stringify(action.zoneDeviceConfig)),
      },
    });
  }

  @Action(CopyPage)
  copyPage(ctx: StateContext<CopyPasteStateModel>, action: CopyPage) {
    this.alertHandlerService.showAlertFn('success', 'Page Copied!');
    return ctx.patchState({
      copiedPage: JSON.parse(JSON.stringify(action.pageConfig)),
    });
  }

  @Action(CopyPersona)
  copyPersona(ctx: StateContext<CopyPasteStateModel>, action: CopyPersona) {
    this.alertHandlerService.showAlertFn('success', 'Persona Copied!');
    return ctx.patchState({
      copiedPersona: JSON.parse(JSON.stringify(action.persona)),
    });
  }

  @Action(PastePage)
  pastePage(ctx: StateContext<CopyPasteStateModel>, action: PastePage) {
    const page = { ...ctx.getState().copiedPage, pageUniqueId: cryptoRandomUUID() } as WorkflowPageConfig;
    if (page) {
      this.workflowEditorService.overwritePage(page, action.shouldNotOverwriteUrl);

      this.alertHandlerService.showAlertFn('success', 'Page Pasted!');
    } else {
      this.alertHandlerService.showAlertFn('error', 'No page has been copied');
    }
  }

  @Action(PastePageProperties)
  pastePageProperties(ctx: StateContext<CopyPasteStateModel>) {
    const page = ctx.getState().copiedPage;

    if (page) {
      this.workflowCommonService.copyPageProperties(page);
      this.updatePageNavigationMapData(page);

      this.alertHandlerService.showAlertFn('success', 'Page Properties Pasted!');
    } else {
      this.alertHandlerService.showAlertFn('error', 'No page has been copied');
    }
  }

  private updatePageNavigationMapData(page: WorkflowPageConfig) {
    const pageNavigationMapService = this.injector.get(PageNavigationMapService);
    const activeConfigService = this.injector.get(ActiveConfigService);
    const pageNavigationMeta = pageNavigationMapService.createPageNavigationMetadataForPage(
      page,
      activeConfigService.activeState,
    );
    pageNavigationMapService.updatePageDataInNavigationMap(
      page.pageUniqueId ?? '',
      activeConfigService.activeState.activeDevice,
      pageNavigationMeta,
    );
  }
}
